import type { PatientNavigatorItemData } from './types';
import type { PatientFilterTab } from './filtering';
import { computePatientQueueCounts, PATIENT_FILTER_TABS, filterPatientsByTab } from './filtering';

export type ScopedPatientTab = PatientFilterTab | 'all';

interface RequestScopedPatientTabOption {
  id: PatientFilterTab;
  label: string;
  count: number;
}

interface AllScopedPatientTabOption {
  id: 'all';
  label: string;
}

export type ScopedPatientTabOption = RequestScopedPatientTabOption | AllScopedPatientTabOption;

export function buildScopedPatientTabs(
  patients: PatientNavigatorItemData[],
  includeAllTab: boolean
): ScopedPatientTabOption[] {
  const tabCounts = computePatientQueueCounts(patients);
  const requestTabs: RequestScopedPatientTabOption[] = PATIENT_FILTER_TABS.reduce<
    RequestScopedPatientTabOption[]
  >((acc, tab) => {
    let count = 0;
    switch (tab.id) {
      case 'urgent':
        count = tabCounts.urgent;
        break;
      case 'routine':
        count = tabCounts.routine;
        break;
      default: {
        const exhaustiveCheck: never = tab.id;
        void exhaustiveCheck;
        return acc;
      }
    }
    if (count === 0) {
      return acc;
    }
    return [...acc, { id: tab.id, label: tab.label, count }];
  }, []);

  if (includeAllTab) {
    return [...requestTabs, { id: 'all', label: 'All' }];
  }
  return requestTabs;
}

export function resolveScopedPatientTab(
  activeTab: ScopedPatientTab,
  tabs: ScopedPatientTabOption[]
): ScopedPatientTab | null {
  const hasActiveTab = tabs.some((tab) => tab.id === activeTab);
  if (hasActiveTab) {
    return activeTab;
  }

  const priorityOrder: ScopedPatientTab[] = ['urgent', 'routine', 'all'];
  for (const tabId of priorityOrder) {
    const isEnabled = tabs.some((tab) => tab.id === tabId);
    if (isEnabled) {
      return tabId;
    }
  }
  return null;
}

export function filterPatientsByScopedTab(
  patients: PatientNavigatorItemData[],
  tab: ScopedPatientTab
): PatientNavigatorItemData[] {
  if (tab === 'all') {
    return patients;
  }
  return filterPatientsByTab(patients, tab);
}
